(() => {
  const VARIANT_DEBUG_PREFIX = "[ProductVariants Debug]";

  const defineCE = (name, ctor, options) => {
    if (!window.customElements.get(name)) {
      window.customElements.define(name, ctor, options);
    }
  };

  const parseHTML = (text) => new DOMParser().parseFromString(text, "text/html");

  const replaceInner = (target, replacement) => {
    if (!target || !replacement) return;
    target.innerHTML = replacement.innerHTML;
  };

  // Some third-party widgets (Recharge included) inject/boot via inline scripts
  // and custom elements. After dynamic section swaps those scripts/elements need a remount.
  const reExecuteScripts = (container) => {
    if (!container) return;
    container.querySelectorAll("script").forEach((oldScript) => {
      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });
  };

  const reRenderWebComponent = (element) => {
    if (!element) return;
    const newElement = element.cloneNode(true);
    element.replaceWith(newElement);
  };

  class ProductVariants extends HTMLElement {
    connectedCallback() {
      this._onChange = this._onChange.bind(this);

      this.productUrl =
        this.getAttribute("data-product-url") ||
        this.getAttribute("product-url") ||
        this.dataset.productUrl ||
        window.location.pathname;

      // Prefer the section instance id (eg. 1234567890...) so we can re-render this exact section instance.
      this.sectionInstanceId =
        this.getAttribute("data-section-id") ||
        this.dataset.sectionId ||
        (this.closest(".shopify-section")?.id || "").replace("shopify-section-", "");

      this.formId = this.getAttribute("form-id") || this.dataset.formId;
      this.form = this.formId ? document.getElementById(this.formId) : null;

      this.variants = this._readVariantsJSON();

      this.addEventListener("change", this._onChange);

      // Initial sync so consumers listening to `variant:changed` get a consistent first state.
      this._applyVariantFromCurrentSelection({ fetch: false, updateUrl: false });
    }

    disconnectedCallback() {
      this.removeEventListener("change", this._onChange);
    }

    _readVariantsJSON() {
      const script =
        this.querySelector('script[type="application/json"][data-variants-json]') ||
        this.querySelector('script[type="application/json"][data-variants]') ||
        this.querySelector('script[type="application/json"][data-variants-json]');
      if (!script) return [];
      try {
        const data = JSON.parse(script.textContent || "[]");
        const variants = Array.isArray(data) ? data : [];
        return variants;
      } catch (e) {
        console.error(VARIANT_DEBUG_PREFIX, "failed to parse variants JSON", e);
        return [];
      }
    }

    _getSelectedOptions() {
      // Works for both radios and selects used by Focal.
      const optionValues = [];
      const optionNames = new Set();

      // Collect option1..option3 order by DOM
      this.querySelectorAll('select[name^="option"], input[type="radio"][name^="option"]').forEach((el) => {
        optionNames.add(el.getAttribute("name"));
      });

      const names = Array.from(optionNames).sort((a, b) => {
        const ai = parseInt(a.replace("option", ""), 10);
        const bi = parseInt(b.replace("option", ""), 10);
        return ai - bi;
      });

      names.forEach((name) => {
        const select = this.querySelector(`select[name="${CSS.escape(name)}"]`);
        if (select) {
          optionValues.push(select.value);
          return;
        }
        const checked = this.querySelector(`input[type="radio"][name="${CSS.escape(name)}"]:checked`);
        optionValues.push(checked ? checked.value : "");
      });

      return optionValues;
    }

    _getSelectedOptionSources() {
      const selected = [];
      const optionNames = new Set();

      this.querySelectorAll('select[name^="option"], input[type="radio"][name^="option"]').forEach((el) => {
        optionNames.add(el.getAttribute("name"));
      });

      const names = Array.from(optionNames).sort((a, b) => {
        const ai = parseInt(a.replace("option", ""), 10);
        const bi = parseInt(b.replace("option", ""), 10);
        return ai - bi;
      });

      names.forEach((name) => {
        const select = this.querySelector(`select[name="${CSS.escape(name)}"]`);
        if (select) {
          const option = select.options?.[select.selectedIndex] || null;
          if (option) selected.push(option);
          return;
        }
        const checked = this.querySelector(`input[type="radio"][name="${CSS.escape(name)}"]:checked`);
        if (checked) selected.push(checked);
      });

      return selected;
    }

    _getSelectedOptionValueIds() {
      return this._getSelectedOptionSources()
        .map((el) => String(el?.dataset?.optionValueId || "").trim())
        .filter(Boolean);
    }

    _getSelectedProductURL() {
      const sourceWithProductUrl = this._getSelectedOptionSources().find((el) => {
        const value = String(el?.dataset?.productUrl || "").trim();
        return value.length > 0;
      });
      return sourceWithProductUrl?.dataset?.productUrl || this.productUrl;
    }

    _findVariantByOptions(options) {
      if (!options?.length || !this.variants?.length) return null;
      return (
        this.variants.find((variant) => {
          if (!variant || !Array.isArray(variant.options)) return false;
          return options.every((value, idx) => (variant.options[idx] || "") === value);
        }) || null
      );
    }

    _getCurrentVariantIdFromForm() {
      if (!this.form) return null;
      const idField =
        this.form.querySelector('input[name="id"]') || this.form.querySelector('select[name="id"]');
      if (!idField) return null;
      const rawValue = String(idField.value || "").trim();
      if (!rawValue) return null;
      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? parsed : null;
    }

    _findVariantById(variantId) {
      if (!variantId || !this.variants?.length) return null;
      return this.variants.find((variant) => Number(variant?.id) === Number(variantId)) || null;
    }

    _setFormVariantId(variantId) {
      if (!this.form) {
        return;
      }
      const idInput = this.form.querySelector('input[name="id"]');
      if (!idInput) {
        return;
      }
      idInput.disabled = false;
      idInput.value = String(variantId);
    }

    _dispatchVariantChanged(variant) {
      document.dispatchEvent(new CustomEvent("variant:changed", { detail: { variant } }));
    }

    _updateOptionValues() {
      const selectedOptions = this._getSelectedOptionSources();
      if (!selectedOptions.length) return;

      selectedOptions.forEach((input) => {
        const valueTargetId = String(input?.dataset?.bindValue || "").trim();
        if (!valueTargetId) return;

        const valueTarget = this.querySelector(`#${CSS.escape(valueTargetId)}`);
        if (!valueTarget) return;

        let selectedValue = "";
        if (input.tagName === "SELECT") {
          selectedValue = input.options?.[input.selectedIndex]?.textContent || input.value || "";
        } else {
          selectedValue = input.value || input.getAttribute("value") || "";
        }

        const normalizedValue = String(selectedValue).trim();
        if (!normalizedValue) return;
        valueTarget.textContent = normalizedValue;
      });
    }

    _updateURL({ variantId, productUrl, optionValueIds }) {
      if (!this.hasAttribute("update-url")) {
        return;
      }
      const baseUrl = productUrl || this.productUrl;
      if (!baseUrl) {
        return;
      }

      const url = new URL(baseUrl, window.location.origin);
      if (optionValueIds?.length) {
        url.searchParams.set("option_values", optionValueIds.join(","));
      } else if (variantId) {
        // Fallback for classic products without option_value ids in markup
        url.searchParams.set("variant", String(variantId));
      }
      window.history.replaceState({}, "", url.toString());
    }

    async _fetchSectionHTML({ variantId, productUrl, optionValueIds }) {
      if (!this.sectionInstanceId) {
        return null;
      }
      const url = new URL(productUrl || this.productUrl, window.location.origin);
      if (optionValueIds?.length) {
        url.searchParams.set("option_values", optionValueIds.join(","));
      } else if (variantId) {
        // Fallback for classic variant resolution
        url.searchParams.set("variant", String(variantId));
      }
      url.searchParams.set("section_id", String(this.sectionInstanceId));

      const res = await fetch(url.toString(), { credentials: "same-origin" });
      const text = await res.text();
      const doc = parseHTML(text);
      const section = doc.getElementById(`shopify-section-${this.sectionInstanceId}`);
      return section;
    }

    _renderUpdateBlocks(newSectionEl) {
      if (!newSectionEl) {
        return;
      }

      const currentSectionEl =
        document.getElementById(`shopify-section-${this.sectionInstanceId}`) ||
        this.closest(".shopify-section");
      if (!currentSectionEl) return;

      newSectionEl.querySelectorAll("[data-update-block]").forEach((incoming) => {
        // mirror the example: if a block opts into inner replacement rules, skip here
        if (incoming.hasAttribute("data-update-block-inner")) return;
        const key = incoming.getAttribute("data-update-block");
        if (!key) return;

        const existing = currentSectionEl.querySelector(`[data-update-block="${CSS.escape(key)}"]`);
        if (existing) {
          existing.innerHTML = incoming.innerHTML;

          // Recharge app block does not always initialize after dynamic updates
          // (e.g. unavailable -> available variant). Force script + component remount.
          if (key === "product-form-block-@app") {
            reExecuteScripts(existing);
            existing
              .querySelectorAll("recharge-subscription-widget, recharge-widget, recharge-bundle-widget")
              .forEach((widgetEl) => reRenderWebComponent(widgetEl));
          }
        }
      });
    }

    _renderUpdateSections({ variantId, productUrl, optionValueIds }) {
      // Global opt-in: any element can request a section refresh by setting data-update-section="<sectionId>"
      document.querySelectorAll("[data-update-section]").forEach((sectionElm) => {
        const sectionId = sectionElm.getAttribute("data-update-section");
        if (!sectionId) return;

        const existingSection = document.getElementById(`shopify-section-${sectionId}`);
        if (!existingSection) return;

        const url = new URL(productUrl || this.productUrl, window.location.origin);
        if (optionValueIds?.length) {
          url.searchParams.set("option_values", optionValueIds.join(","));
        } else if (variantId) {
          url.searchParams.set("variant", String(variantId));
        }
        url.searchParams.set("section_id", String(sectionId));

        fetch(url.toString(), { credentials: "same-origin" })
          .then((r) => r.text())
          .then((text) => {
            const doc = parseHTML(text);
            const incoming = doc.getElementById(`shopify-section-${sectionId}`);
            if (incoming) {
              replaceInner(existingSection, incoming);
            }
          })
          .catch((error) => {
            console.error(VARIANT_DEBUG_PREFIX, "failed to update external section", {
              sectionId,
              variantId,
              error,
            });
          });
      });
    }

    async _applyVariantFromCurrentSelection({ fetch, updateUrl }) {
      const options = this._getSelectedOptions();
      const optionValueIds = this._getSelectedOptionValueIds();
      const selectedProductUrl = this._getSelectedProductURL();
      const currentVariantId = this._getCurrentVariantIdFromForm();
      const canResolveByOptionValues = Boolean(selectedProductUrl && optionValueIds?.length);

      // Resolve by the actively selected option controls first.
      // The hidden form id can lag behind during rapid option changes.
      let variant = this._findVariantByOptions(options);
      if (!variant) {
        variant = this._findVariantById(currentVariantId);
      }
      if (!variant && !canResolveByOptionValues) {
        return;
      }

      const effectiveVariantId = variant?.id || currentVariantId;

      if (variant?.id) {
        this._setFormVariantId(variant.id);
      }
      this._updateOptionValues();
      if (variant) {
        this._dispatchVariantChanged(variant);
      }

      if (updateUrl) {
        this._updateURL({
          variantId: effectiveVariantId,
          productUrl: selectedProductUrl,
          optionValueIds,
        });
      }

      if (fetch) {
        try {
          const newSectionEl = await this._fetchSectionHTML({
            variantId: effectiveVariantId,
            productUrl: selectedProductUrl,
            optionValueIds,
          });
          this._renderUpdateBlocks(newSectionEl);
          this._renderUpdateSections({
            variantId: effectiveVariantId,
            productUrl: selectedProductUrl,
            optionValueIds,
          });
        } catch (e) {
          console.error(VARIANT_DEBUG_PREFIX, "failed during section update after variant change", {
            variantId: effectiveVariantId,
            error: e,
          });
          // no-op: variant change should still work even if section fetch fails
        }
      }
    }

    async _onChange(event) {
      await this._applyVariantFromCurrentSelection({ fetch: true, updateUrl: true });
    }
  }

  defineCE("product-variants", ProductVariants);

  // ---
  // Media gallery (Focal-like): product-media + flickity-carousel + flickity-controls
  // ---
  const loadScriptOnce = (() => {
    const loaded = new Map();
    return (src) => {
      if (!src) return Promise.reject(new Error("Missing script src"));
      if (loaded.has(src)) return loaded.get(src);
      const p = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(s);
      });
      loaded.set(src, p);
      return p;
    };
  })();

  const getFlickity = async () => {
    if (window.ThemeFlickity) return window.ThemeFlickity;
    const src = window.themeVariables?.libs?.flickity;
    if (src) {
      await loadScriptOnce(src);
    }
    return window.ThemeFlickity || null;
  };

  class FlickityCarousel extends HTMLElement {
    async connectedCallback() {
      if (this._initPromise) return;
      this._initPromise = this._init();
      await this._initPromise;
    }

    disconnectedCallback() {
      try {
        this._flkty?.destroy?.();
      } catch (_) {}
      this._flkty = null;
    }

    async _init() {
      const Flickity = await getFlickity();
      if (!Flickity) return;

      let config = {};
      const raw = this.getAttribute("flickity-config");
      if (raw) {
        try {
          config = JSON.parse(raw);
        } catch (_) {
          config = {};
        }
      }

      // Flickity expects a real element containing the cells.
      this._flkty = new Flickity(this, config);
      this.flickity = this._flkty; // convenient handle for other elements

      // Ensure initialIndex selector works even if markup has multiple classes.
      this._flkty.resize();
    }
  }

  class FlickityControls extends HTMLElement {
    connectedCallback() {
      this._onClick = this._onClick.bind(this);
      this.addEventListener("click", this._onClick);

      this._carousel = document.getElementById(this.getAttribute("controls"));
      this._bindCarousel();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this._onClick);
      try {
        this._flkty?.off?.("select", this._onSelect);
      } catch (_) {}
    }

    _bindCarousel() {
      if (!this._carousel) return;

      const maybeBind = () => {
        const flkty = this._carousel.flickity;
        if (!flkty) return false;
        this._flkty = flkty;
        this._onSelect = () => this._syncSelectedUI();
        this._flkty.on("select", this._onSelect);
        this._syncSelectedUI();
        return true;
      };

      if (maybeBind()) return;

      // Flickity script might be lazy-loaded; retry briefly.
      const started = Date.now();
      const t = window.setInterval(() => {
        if (maybeBind() || Date.now() - started > 5000) {
          window.clearInterval(t);
        }
      }, 50);
    }

    _syncSelectedUI() {
      if (!this._carousel || !this._flkty) return;
      const selectedEl = this._flkty.selectedElement;
      const selectedMediaId = selectedEl?.getAttribute?.("data-media-id");
      if (!selectedMediaId) return;

      this.querySelectorAll('[data-action="select"][aria-current]').forEach((btn) => {
        btn.removeAttribute("aria-current");
      });

      this.querySelectorAll(`[data-action="select"][data-media-id="${CSS.escape(selectedMediaId)}"]`).forEach((btn) => {
        btn.setAttribute("aria-current", "true");
      });
    }

    _onClick(event) {
      const target = event.target.closest("[data-action]");
      if (!target || !this._flkty) return;

      const action = target.getAttribute("data-action");
      if (action === "prev") {
        event.preventDefault();
        this._flkty.previous(true);
        return;
      }
      if (action === "next") {
        event.preventDefault();
        this._flkty.next(true);
        return;
      }
      if (action === "select") {
        event.preventDefault();
        const controlsId = target.getAttribute("aria-controls");
        if (controlsId) {
          this._flkty.selectCell(`#${controlsId}`);
        }
      }
    }
  }

  class ProductMedia extends HTMLElement {
    connectedCallback() {
      this._onVariantChanged = this._onVariantChanged.bind(this);
      document.addEventListener("variant:changed", this._onVariantChanged);

      this.carousel = this.querySelector("flickity-carousel");
      this.controls = this.querySelector("flickity-controls");
      this.zoomButton = this.querySelector(".product__zoom-button");
    }

    disconnectedCallback() {
      document.removeEventListener("variant:changed", this._onVariantChanged);
    }

    _onVariantChanged(event) {
      const variant = event.detail?.variant;
      const mediaId = variant?.featured_media?.id;
      if (!mediaId) return;

      // Select the matching slide if it exists.
      const slide = this.querySelector(`.product__media-item[data-media-id="${CSS.escape(String(mediaId))}"]`);
      if (slide && this.carousel?.flickity) {
        try {
          this.carousel.flickity.selectCell(slide);
        } catch (_) {}
      }

      // Toggle zoom button depending on selected media type
      const selected = this.carousel?.flickity?.selectedElement;
      const type = selected?.getAttribute?.("data-media-type");
      if (this.zoomButton) {
        if (type && type !== "image") {
          this.zoomButton.setAttribute("hidden", "");
        } else {
          this.zoomButton.removeAttribute("hidden");
        }
      }
    }
  }

  defineCE("flickity-carousel", FlickityCarousel);
  defineCE("flickity-controls", FlickityControls);
  defineCE("product-media", ProductMedia);

  // `is="product-form"` submit behavior is centralized in theme-core.js global submit handler.
})();

